import React, { useState, useEffect, useRef } from 'react';
import { propertiesAPI, leadsAPI } from '../services/api';
import { useToast } from './Toast';
import { Plus, X, Building2, MapPin, BedDouble, Bath, Square, Edit2, Trash2, Check, Home, Building, Map, Search, Image as ImageIcon, Settings2, Car, Clock, User, Camera } from 'lucide-react';

const normalizeImageList = (imagesStr) => {
  if (!imagesStr) return [];
  if (Array.isArray(imagesStr)) return imagesStr;
  const str = String(imagesStr);
  if (str.includes('\n')) {
    return str.split('\n').map(s => s.trim()).filter(Boolean);
  }
  return [str.trim()].filter(Boolean);
};

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api\/?$/, '');

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url) || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
};

const formatSouthAsianNumber = (num) => {
  const str = String(num).replace(/,/g, '');
  if (!str) return '';
  const [whole, decimal] = str.split('.');
  const lastThree = whole.slice(-3);
  const rest = whole.slice(0, -3);
  if (rest) {
    const groups = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    return `${groups},${lastThree}${decimal ? '.' + decimal : ''}`;
  }
  return lastThree + (decimal ? '.' + decimal : '');
};

const formatPkrLabel = (num) => {
  const n = Number(String(num).replace(/,/g, ''));
  if (isNaN(n) || n <= 0) return null;
  if (n < 1000) return String(n);
  if (n < 100000) {
    const val = n / 1000;
    const formatted = val % 1 === 0 ? val.toFixed(0) : val.toFixed(1);
    return `${formatted} K`;
  }
  if (n < 10000000) {
    const val = n / 100000;
    const formatted = val % 1 === 0 ? val.toFixed(0) : val.toFixed(1);
    return `${formatted} Lac`;
  }
  if (n < 1000000000) {
    const val = n / 10000000;
    const formatted = val % 1 === 0 ? val.toFixed(0) : val.toFixed(1);
    return `${formatted} Crore`;
  }
  const val = n / 1000000000;
  const formatted = val % 1 === 0 ? val.toFixed(0) : val.toFixed(1);
  return `${formatted} Arab`;
};

const Properties = () => {
  const { showToast } = useToast();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minArea, setMinArea] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [bedroomFilter, setBedroomFilter] = useState('');
  const [bathroomFilter, setBathroomFilter] = useState('');
  const [amenities, setAmenities] = useState({ parking: false, petAllowed: false });
  const [editingProperty, setEditingProperty] = useState(null);
  const [sellingProperty, setSellingProperty] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const isAdmin = currentUser?.role === 'admin';
  const isEditor = currentUser?.role === 'editor';
  const canEdit = isAdmin || isEditor;
  const canDelete = isAdmin;

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await propertiesAPI.getAll();
      setProperties(response.data || []);
    } catch (error) {
      console.error('Failed to load properties:', error);
      showToast(error.response?.data?.error || 'Failed to load properties', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      await propertiesAPI.delete(id);
      showToast('Property deleted successfully', 'success');
      loadProperties();
    } catch (error) {
      console.error('Failed to delete property:', error);
      showToast(error.response?.data?.error || 'Failed to delete property', 'error');
    }
  };

  const handleStatusChange = async (property, newStatus) => {
    if (property.status === newStatus) return;

    if (newStatus === 'sold') {
      setSellingProperty(property);
      return;
    }
    
    if (newStatus === 'reserved') {
      if (!window.confirm(`Mark ${property.title} as Reserved?`)) {
        loadProperties();
        return;
      }
    }
    
    if (newStatus === 'available' && property.status !== 'available') {
      if (!window.confirm(`Mark ${property.title} as Available again?`)) {
        loadProperties();
        return;
      }
    }

    try {
      await propertiesAPI.update(property.id, { status: newStatus });
      showToast('Status updated', 'success');
      loadProperties();
    } catch (error) {
      console.error('Failed to update status:', error);
      showToast(error.response?.data?.error || 'Failed to update status', 'error');
      loadProperties();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', { 
      style: 'currency', 
      currency: 'PKR',
      minimumFractionDigits: 0 
    }).format(amount || 0);
  };

  const filteredProperties = properties.filter(p => {
    const searchLower = searchQuery.trim().toLowerCase();
    const matchesSearch = !searchLower || [p.title, p.location, p.property_type].some(value => String(value || '').toLowerCase().includes(searchLower));
    if (!matchesSearch) return false;

    if (filterTab !== 'all' && p.status !== filterTab) return false;

    const typeMatches = typeFilter === 'all' || !typeFilter || String(p.property_type || '').toLowerCase().includes(typeFilter);
    if (!typeMatches) return false;

    const priceMatches = (!minPrice || Number(p.price) >= Number(minPrice)) && (!maxPrice || Number(p.price) <= Number(maxPrice));
    if (!priceMatches) return false;

    const areaMatches = (!minArea || Number(p.area_sqft) >= Number(minArea)) && (!maxArea || Number(p.area_sqft) <= Number(maxArea));
    if (!areaMatches) return false;

    const bedroomMatches = !bedroomFilter || Number(p.bedrooms) >= Number(bedroomFilter);
    if (!bedroomMatches) return false;

    const bathroomMatches = !bathroomFilter || Number(p.bathrooms) >= Number(bathroomFilter);
    if (!bathroomMatches) return false;

    const features = String(p.features || '').toLowerCase();
    if (amenities.parking && !features.includes('parking')) return false;
    if (amenities.petAllowed && !features.includes('pet')) return false;

    return true;
  });

  const handleAmenityToggle = (key) => {
    setAmenities(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilterTab('all');
    setTypeFilter('all');
    setMinPrice('');
    setMaxPrice('');
    setMinArea('');
    setMaxArea('');
    setBedroomFilter('');
    setBathroomFilter('');
    setAmenities({ parking: false, petAllowed: false });
  };

  const getFirstImage = (imagesStr) => {
    const urls = normalizeImageList(imagesStr);
    return urls.length > 0 ? urls[0] : null;
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Properties</h1>

          <div className="flex items-center gap-3 flex-wrap">
              <div className="relative w-full sm:w-[320px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search properties...."
                style={{ paddingLeft: '3rem' }}
                className="w-full pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-[13px] placeholder:text-slate-400 focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] outline-none transition-all shadow-sm"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <span className="text-[10px] font-bold text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-slate-50">⌘1</span>
              </div>
            </div>
            
            <button
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm"
            >
              <Settings2 size={16} className="text-slate-500" />
              Advance Filter
            </button>

            {canEdit && (
              <button
                onClick={() => setEditingProperty({})}
                className="flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-[#1E3A5F] rounded-xl hover:bg-[#132845] transition-all shadow-sm shrink-0"
              >
                <Plus size={16} strokeWidth={2.5} />
                Create Property
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-8">
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1E3A5F]"></div>
              </div>
            ) : filteredProperties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white border border-slate-200 border-dashed rounded-3xl shadow-sm">
            <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4 text-slate-400">
              <Building2 size={32} strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1">No properties found</h3>
            <p className="text-[14px] text-slate-500 mb-6">Get started by adding a new property to your portfolio.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredProperties.map(property => {
              const firstImg = getFirstImage(property.images);
              const createdDate = property.created_at ? new Date(property.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
              
              const getBadgeStyle = (status) => {
                switch(status) {
                  case 'available': return "bg-white/95 text-emerald-600 border-emerald-100";
                  case 'sold': return "bg-white/95 text-rose-600 border-rose-100";
                  case 'reserved': return "bg-white/95 text-amber-600 border-amber-100";
                  default: return "bg-white/95 text-slate-600 border-slate-100";
                }
              };

              return (
                <div key={property.id} className="group bg-white rounded-2xl shadow-sm border border-slate-200 hover:shadow-md transition-all duration-300 p-3 flex flex-col sm:flex-row gap-5 cursor-pointer relative" onClick={(e) => {
                  if (!e.target.closest('button') && !e.target.closest('select') && canEdit) {
                    setEditingProperty(property);
                  }
                }}>
                  <div className="w-full sm:w-[240px] h-[160px] bg-slate-100 rounded-xl overflow-hidden relative shrink-0">
                    {firstImg ? (
                      <img
                        src={resolveImageUrl(firstImg)}
                        alt={property.title}
                        onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-property.png'; }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : null}
                    {!firstImg && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-slate-100">
                        <Camera size={28} strokeWidth={1.5} />
                        <span className="text-[11px] mt-1.5 font-medium">No Image</span>
                      </div>
                    )}
                    <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1.5 shadow-sm border backdrop-blur-md ${getBadgeStyle(property.status)}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${property.status === 'available' ? 'bg-emerald-500' : property.status === 'sold' ? 'bg-rose-500' : property.status === 'reserved' ? 'bg-amber-500' : 'bg-slate-400'}`}></span>
                      <span className="capitalize">{property.status}</span>
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col justify-center py-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-slate-900 font-bold text-[16px] leading-tight mb-1.5 group-hover:text-[#1E3A5F] transition-colors">
                          {property.title}
                        </h3>
                        <div className="flex items-center gap-1.5 text-[13px] text-slate-500 mb-4">
                          <MapPin size={14} className="text-[#1E3A5F] shrink-0" />
                          <span className="line-clamp-1">{property.location || 'No location specified'}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end shrink-0 gap-2">
                        <div className="text-slate-900 font-bold text-[18px]">
                          {formatCurrency(property.price)}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <select
                            value={property.status}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); handleStatusChange(property, e.target.value); }}
                            className="appearance-none bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg pl-2 pr-6 py-1 text-[11px] font-bold text-slate-600 focus:ring-1 focus:ring-[#1E3A5F] outline-none cursor-pointer"
                          >
                            <option value="available">Available</option>
                            <option value="sold">Sold</option>
                            <option value="reserved">Reserved</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          {canEdit && (
                            <button onClick={(e) => { e.stopPropagation(); setEditingProperty(property); }} className="p-1.5 text-slate-400 hover:text-[#1E3A5F] hover:bg-[#1E3A5F]/10 rounded-lg transition-colors">
                              <Edit2 size={14} />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(property.id); }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-auto">
                      <div className="flex items-center gap-1.5 text-slate-600 text-[13px]">
                        <BedDouble size={16} className="text-slate-400" strokeWidth={2} />
                        <span className="font-semibold">{property.bedrooms || '0'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600 text-[13px]">
                        <Bath size={16} className="text-slate-400" strokeWidth={2} />
                        <span className="font-semibold">{property.bathrooms || '0'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600 text-[13px]">
                        <Car size={16} className="text-slate-400" strokeWidth={2} />
                        <span className="font-semibold">{property.features?.toLowerCase().includes('parking') ? '1' : '0'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600 text-[13px]">
                        <Square size={16} className="text-slate-400" strokeWidth={2} />
                        <span className="font-semibold">{property.area_sqft ? `${property.area_sqft} sqft` : '-'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-500 text-[13px] ml-1">
                        <Clock size={15} className="text-slate-400" />
                        <span className="font-medium">{createdDate}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600 text-[13px] ml-3">
                        <User size={15} className="text-slate-400" />
                        <span className="font-medium">{property.owner_name || 'Admin'}</span>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>

          {isFilterOpen && (
            <>
              <div 
                className="fixed inset-0 bg-slate-900/20 z-40 backdrop-blur-sm transition-opacity"
                onClick={() => setIsFilterOpen(false)}
              ></div>
              
              <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200">
                <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#1E3A5F] flex items-center justify-center">
                      <Settings2 size={18} className="text-white" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-900">Filters</h2>
                  </div>
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                  <div>
                    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Property Type</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {['house', 'apartment', 'commercial', 'land'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setTypeFilter(typeFilter === option ? 'all' : option)}
                          className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition ${typeFilter === option ? 'border-[#1E3A5F] text-[#1E3A5F] bg-[#1E3A5F]/5 shadow-sm' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                        >
                          {option === 'house' && <Home size={16} />}
                          {option === 'apartment' && <Building size={16} />}
                          {option === 'commercial' && <Building2 size={16} />}
                          {option === 'land' && <Map size={16} />}
                          <span className="capitalize">{option}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Rooms</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[13px] font-semibold text-slate-700 mb-2">Bedroom</label>
                        <div className="flex items-center justify-between border border-slate-200 rounded-xl px-1 py-1">
                          <button onClick={() => setBedroomFilter(prev => Math.max(0, Number(prev || 0) - 1).toString())} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-50 rounded-lg"><span className="text-lg leading-none">-</span></button>
                          <span className="text-[14px] font-bold text-slate-800 w-8 text-center">{bedroomFilter || '0'}</span>
                          <button onClick={() => setBedroomFilter(prev => (Number(prev || 0) + 1).toString())} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-50 rounded-lg"><Plus size={16} /></button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-slate-700 mb-2">Bathroom</label>
                        <div className="flex items-center justify-between border border-slate-200 rounded-xl px-1 py-1">
                          <button onClick={() => setBathroomFilter(prev => Math.max(0, Number(prev || 0) - 1).toString())} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-50 rounded-lg"><span className="text-lg leading-none">-</span></button>
                          <span className="text-[14px] font-bold text-slate-800 w-8 text-center">{bathroomFilter || '0'}</span>
                          <button onClick={() => setBathroomFilter(prev => (Number(prev || 0) + 1).toString())} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#1E3A5F] hover:bg-slate-50 rounded-lg"><Plus size={16} /></button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Price Range</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input
                          type="number"
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                          placeholder="Min $"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] font-semibold focus:bg-white focus:border-[#1E3A5F] focus:ring-2 focus:ring-[#1E3A5F]/20 outline-none"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          placeholder="Max $"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] font-semibold focus:bg-white focus:border-[#1E3A5F] focus:ring-2 focus:ring-[#1E3A5F]/20 outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Area Range (sqft)</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input
                          type="number"
                          value={minArea}
                          onChange={(e) => setMinArea(e.target.value)}
                          placeholder="Min sqft"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] font-semibold focus:bg-white focus:border-[#1E3A5F] focus:ring-2 focus:ring-[#1E3A5F]/20 outline-none"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          value={maxArea}
                          onChange={(e) => setMaxArea(e.target.value)}
                          placeholder="Max sqft"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] font-semibold focus:bg-white focus:border-[#1E3A5F] focus:ring-2 focus:ring-[#1E3A5F]/20 outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Amenities</h3>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={amenities.parking}
                          onChange={() => handleAmenityToggle('parking')}
                          className="h-4 w-4 rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                        />
                        <span className="text-[14px] font-medium text-slate-700">Parking slot</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={amenities.petAllowed}
                          onChange={() => handleAmenityToggle('petAllowed')}
                          className="h-4 w-4 rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                        />
                        <span className="text-[14px] font-medium text-slate-700">Pet allowed</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="p-5 border-t border-slate-100 bg-slate-50 flex items-center gap-3 shrink-0">
                  <button
                    onClick={resetFilters}
                    className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-[13px] hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="flex-[2] px-4 py-2.5 bg-[#1E3A5F] text-white rounded-xl font-bold text-[13px] hover:bg-[#132845] transition-colors shadow-sm"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </>
          )}

        </div>

        {editingProperty && (
          <PropertyModal 
            property={editingProperty} 
            onClose={() => setEditingProperty(null)} 
            onSaved={(saved) => {
              setEditingProperty(null);
              if (editingProperty?.id && saved?.id) {
                setProperties(prev => {
                  const idx = prev.findIndex(p => p.id === saved.id);
                  if (idx !== -1) {
                    const next = [...prev];
                    next[idx] = { ...next[idx], ...saved };
                    return next;
                  }
                  return prev;
                });
              } else {
                loadProperties();
              }
            }}
          />
        )}

        {sellingProperty && (
          <SoldCaptureModal
            property={sellingProperty}
            onClose={() => {
              setSellingProperty(null);
              loadProperties();
            }}
            onConfirm={() => {
              setSellingProperty(null);
              loadProperties();
            }}
          />
        )}
      </div>
    </div>
  );
};

const PropertyModal = ({ property, onClose, onSaved }) => {
  const { showToast } = useToast();
  const isEdit = !!property.id;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const fileInputRef = useRef(null);
  const blobUrlRef = useRef([]);

  const initialImages = property.images ? normalizeImageList(property.images).join('\n') : '';
  const initialFormDataRef = useRef({
    title: property.title || '',
    property_type: property.property_type || 'apartment',
    location: property.location || '',
    price: property.price || '',
    negotiable: property.negotiable ?? true,
    bedrooms: property.bedrooms || '',
    bathrooms: property.bathrooms || '',
    area_sqft: property.area_sqft || '',
    status: property.status || 'available',
    description: property.description || '',
    images: initialImages,
    features: property.features || ''
  });

  const [formData, setFormData] = useState({ ...initialFormDataRef.current });
  const [newFiles, setNewFiles] = useState([]);

  const isDirty = (() => {
    const init = initialFormDataRef.current;
    if (newFiles.length > 0) return true;
    return Object.keys(init).some(k => String(init[k] || '') !== String(formData[k] || ''));
  })();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (saveError) setSaveError(null);
  };

  const handlePriceChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, price: raw }));
    if (saveError) setSaveError(null);
  };

  const displayPrice = formData.price ? formatSouthAsianNumber(formData.price) : '';
  const pkrLabel = formatPkrLabel(formData.price);

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });

  useEffect(() => {
    return () => {
      blobUrlRef.current.forEach(u => URL.revokeObjectURL(u));
      blobUrlRef.current = [];
    };
  }, []);

  const MAX_IMAGES = 10;
  const MAX_FILE_SIZE_MB = 5;

  const handleImageFiles = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const existingCount = normalizeImageList(formData.images).length;
    const totalAfterAdd = existingCount + newFiles.length + files.length;
    if (totalAfterAdd > MAX_IMAGES) {
      showToast(`Maximum ${MAX_IMAGES} images allowed`, 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const oversized = files.find(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (oversized) {
      showToast(`Each image must be under ${MAX_FILE_SIZE_MB}MB`, 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const newBlobs = files.map(f => URL.createObjectURL(f));
    blobUrlRef.current.push(...newBlobs);
    setNewFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (saveError) setSaveError(null);
  };

  const previewUrls = [
    ...normalizeImageList(formData.images).map(u => resolveImageUrl(u) || u).filter(Boolean),
    ...newFiles.map((_, i) => blobUrlRef.current[i]).filter(Boolean)
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    if (!formData.title || !formData.price) {
      showToast('Title and Price are required', 'error');
      return;
    }

    const existingUrls = normalizeImageList(formData.images);
    const allUrls = [...existingUrls];

    if (newFiles.length > 0) {
      try {
        const newDataUrls = await Promise.all(newFiles.map(readFileAsDataUrl));
        allUrls.push(...newDataUrls);
      } catch (error) {
        showToast('Failed to read selected image files', 'error');
        return;
      }
    }

    if (allUrls.length > 0) {
      const invalidUrl = allUrls.find(u => {
        if (u.startsWith('data:image/')) return false;
        const withoutQuery = u.split('?')[0];
        return !/\.(jpg|jpeg|png|webp)(\?.*)?$/i.test(withoutQuery);
      });
      if (invalidUrl) {
        showToast('Each image URL must end in jpg, jpeg, png, or webp', 'error');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = { ...formData, images: allUrls.join('\n') };

      let saved;
      if (isEdit) {
        await propertiesAPI.update(property.id, payload);
        saved = { ...payload, id: property.id };
        showToast('Property updated successfully', 'success');
      } else {
        const res = await propertiesAPI.create(payload);
        saved = { ...payload, id: res.data?.id };
        showToast('Property created successfully', 'success');
      }
      blobUrlRef.current.forEach(u => URL.revokeObjectURL(u));
      blobUrlRef.current = [];
      onSaved(saved);
    } catch (error) {
      const status = error.response?.status;
      const msg = status === 413
        ? 'Images are too large. Use smaller images or upload fewer at a time.'
        : (error.response?.data?.error || 'Failed to save property');
      setSaveError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = (index) => {
    const existing = normalizeImageList(formData.images);
    if (index < existing.length) {
      existing.splice(index, 1);
      setFormData(prev => ({ ...prev, images: existing.join('\n') }));
    } else {
      const fileIndex = index - existing.length;
      if (blobUrlRef.current[fileIndex]) {
        URL.revokeObjectURL(blobUrlRef.current[fileIndex]);
      }
      blobUrlRef.current.splice(fileIndex, 1);
      setNewFiles(prev => prev.filter((_, i) => i !== fileIndex));
    }
    if (saveError) setSaveError(null);
  };

  const handleClose = () => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    }
    blobUrlRef.current.forEach(u => URL.revokeObjectURL(u));
    blobUrlRef.current = [];
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 shrink-0">
          <h2 className="text-xl font-extrabold text-[#1E3A5F]">
            {isEdit ? 'Edit Property' : 'Add New Property'}
          </h2>
          <button type="button" onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <form id="property-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="col-span-1 md:col-span-2">
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Property Title <span className="text-rose-500">*</span></label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. Luxury 3-Bed Apartment in DHA"
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
              />
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Property Type</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleChange}
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none appearance-none"
              >
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="plot">Plot / Land</option>
                <option value="commercial">Commercial</option>
                <option value="office">Office</option>
                <option value="studio">Studio</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Initial Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none appearance-none"
              >
                <option value="available">Available</option>
                <option value="sold">Sold</option>
                <option value="reserved">Reserved</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Location Area</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g. DHA Phase 6"
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
              />
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Asking Price (PKR) <span className="text-rose-500">*</span></label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold pointer-events-none">Rs</span>
                <input
                  type="text"
                  inputMode="numeric"
                  name="price"
                  required
                  value={displayPrice}
                  onChange={handlePriceChange}
                  placeholder="0"
                  style={{ paddingLeft: '3rem' }}
                  className="w-full border border-slate-200 bg-slate-50 rounded-xl pr-4 py-2.5 text-[14px] font-bold text-[#1E3A5F] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
                />
              </div>
              {pkrLabel && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    ≈ {pkrLabel}
                  </span>
                </div>
              )}
              <label className="flex items-center gap-2 mt-2.5 text-[13px] font-semibold text-slate-600 cursor-pointer w-max">
                <input
                  type="checkbox"
                  name="negotiable"
                  checked={formData.negotiable}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                />
                Price is Negotiable
              </label>
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Bedrooms</label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleChange}
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
              />
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Bathrooms</label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleChange}
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
              />
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Covered Area (sqft)</label>
              <input
                type="number"
                name="area_sqft"
                value={formData.area_sqft}
                onChange={handleChange}
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
              />
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Key Features</label>
              <input
                type="text"
                name="features"
                value={formData.features}
                onChange={handleChange}
                placeholder="e.g. Pool, Corner Plot"
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
              />
            </div>

            <div className="col-span-1 md:col-span-2">
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Property Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Highlight the key selling points..."
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none resize-none"
              />
            </div>

            <div className="col-span-1 md:col-span-2 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 text-[13px] font-bold text-slate-700">
                  <ImageIcon size={16} className="text-[#1E3A5F]" />
                  Property Images
                </div>
              </div>
              <input
                type="file"
                accept="image/jpg,image/jpeg,image/png,image/webp"
                multiple
                ref={fileInputRef}
                onChange={handleImageFiles}
                className="block w-full text-[13px] text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#1E3A5F] file:text-white hover:file:bg-[#132845] cursor-pointer"
              />
              <p className="text-[11px] text-slate-400 mt-1.5">Max {MAX_IMAGES} images, {MAX_FILE_SIZE_MB}MB each (JPG, PNG, WebP)</p>
              {previewUrls.length > 0 ? (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {previewUrls.map((url, i) => (
                    <div key={i} className="relative rounded-xl overflow-hidden border border-slate-200 shadow-sm h-20 bg-white group">
                      <img
                        src={url}
                        alt={`Preview ${i + 1}`}
                        onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-property.png'; }}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(i)}
                        className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-black/70 transition-all"
                      >
                        <X size={12} strokeWidth={3} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 flex flex-col items-center justify-center py-8 bg-slate-100 rounded-xl border border-slate-200 border-dashed relative overflow-hidden group">
                  <div className="relative z-10 flex flex-col items-center">
                    <Camera size={32} className="text-slate-300" strokeWidth={1.5} />
                    <span className="text-[13px] font-semibold text-slate-500 mt-2">No images uploaded</span>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="px-6 py-5 border-t border-slate-100 shrink-0 bg-slate-50 rounded-b-3xl">
          {saveError && (
            <p className="text-[12px] text-red-600 font-semibold mb-3 text-center">{saveError}</p>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="px-6 py-2.5 border border-slate-200 text-slate-600 bg-white rounded-xl hover:bg-slate-100 text-[13px] font-bold transition-colors shadow-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="property-form"
              disabled={saving}
              className="px-6 py-2.5 bg-[#1E3A5F] hover:bg-[#132845] text-white rounded-xl text-[13px] font-bold disabled:opacity-50 transition-all shadow-sm flex items-center gap-2"
            >
              {saving && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
              {isEdit ? 'Save Changes' : 'Publish Property'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SoldCaptureModal = ({ property, onClose, onConfirm }) => {
  const { showToast } = useToast();
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    linkedLeadId: '',
    buyerName: '',
    salePrice: property.price || '',
    saleDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await leadsAPI.getAll({ status: 'open' });
        const raw = res.data;
        const leadsArray = Array.isArray(raw) ? raw : Array.isArray(raw?.leads) ? raw.leads : Array.isArray(raw?.data) ? raw.data : [];
        setLeads(leadsArray);
      } catch(e) {
        console.error(e);
      }
    };
    fetchLeads();
  }, []);

  const filteredLeads = leads.filter(l => {
    if (!searchTerm) return false;
    const term = searchTerm.toLowerCase();
    const nameMatch = l.name && l.name.toLowerCase().includes(term);
    const phoneMatch = l.phone && l.phone.includes(term);
    return nameMatch || phoneMatch;
  });

  const handleLeadSelect = (lead) => {
    setFormData(prev => ({
      ...prev,
      linkedLeadId: lead.id,
      buyerName: lead.name
    }));
    setSearchTerm(lead.name);
    setShowDropdown(false);
  };

  const handleSubmit = async () => {
    if (!formData.buyerName) {
      showToast('Buyer Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await propertiesAPI.update(property.id, {
        status: 'sold',
        sold_to_lead_id: formData.linkedLeadId || null,
        buyer_name: formData.buyerName,
        sale_price: formData.salePrice || property.price,
        sale_date: formData.saleDate,
        sale_notes: formData.notes
      });
      if (formData.linkedLeadId) {
        await leadsAPI.update(formData.linkedLeadId, { status: 'closed' });
      }
      showToast('Property marked as sold 🎉', 'success');
      onConfirm();
    } catch(error) {
      showToast(error.response?.data?.error || 'Failed to mark as sold', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        <div className="bg-[#1E3A5F] p-6 shrink-0 relative overflow-hidden">
          <div className="absolute -right-4 -top-4 opacity-10">
            <Check size={100} strokeWidth={3} />
          </div>
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <h2 className="text-2xl font-extrabold text-white">Record Sale</h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors">
                <X size={18} strokeWidth={3} />
              </button>
            </div>
            <p className="text-blue-100 mt-1 font-medium text-sm line-clamp-1">{property.title}</p>
          </div>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 flex-1 bg-white">
          <div className="relative">
            <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Link to Existing Lead</label>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(e.target.value.length >= 2);
                  if (e.target.value === '') {
                    setFormData(prev => ({...prev, linkedLeadId: ''}));
                  }
                }}
                placeholder="Search by name or phone..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] outline-none transition-all"
              />
            </div>
            {showDropdown && searchTerm.length >= 2 && (
              <div className="absolute z-20 w-full mt-2 bg-white border border-slate-100 rounded-xl shadow-xl max-h-56 overflow-y-auto p-1">
                {filteredLeads.length > 0 ? (
                  filteredLeads.map(l => (
                    <div 
                      key={l.id} 
                      onClick={() => handleLeadSelect(l)}
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer rounded-lg transition-colors flex items-center justify-between"
                    >
                      <div>
                        <div className="font-bold text-slate-800 text-[14px]">{l.name}</div>
                        <div className="text-slate-500 text-[12px] mt-0.5 font-medium">{l.phone || 'No phone'}</div>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-[#1E3A5F]/10 text-[#1E3A5F] flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <Plus size={14} strokeWidth={3} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-4 text-[13px] text-slate-500 text-center font-medium">No matches found</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Buyer Name <span className="text-rose-500">*</span></label>
            <input
              type="text"
              required
              value={formData.buyerName}
              onChange={(e) => setFormData({...formData, buyerName: e.target.value})}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[14px] font-semibold focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] outline-none transition-all"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Final Sale Price</label>
              <input
                type="number"
                value={formData.salePrice}
                onChange={(e) => setFormData({...formData, salePrice: e.target.value})}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[14px] font-bold text-[#1E3A5F] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Sale Date</label>
              <input
                type="date"
                value={formData.saleDate}
                onChange={(e) => setFormData({...formData, saleDate: e.target.value})}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[14px] font-semibold focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] outline-none transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Closing Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={3}
              placeholder="Any specifics about the deal..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] outline-none transition-all resize-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 shrink-0 rounded-b-3xl">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-slate-200 text-slate-600 bg-white rounded-xl hover:bg-slate-100 text-[13px] font-bold transition-colors shadow-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2.5 bg-[#1E3A5F] hover:bg-[#132845] text-white rounded-xl text-[13px] font-bold disabled:opacity-50 transition-all shadow-sm flex items-center gap-2"
          >
            {saving ? 'Saving...' : 'Confirm Sale ✓'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Properties;